import { useDisclosure } from '@chakra-ui/react'
import type React from 'react'
import { useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import AddCircleIcon from '../../../../../assets/icons/add-circle-stroke-rounded'
import PageWrapper from '../../../../../components/Pagewrapper'
import Table from '../../../../../components/Table/Table'
import {
  useGetFeeStructureByIdQuery,
  useGetFeeStructuresQuery,
} from '../../../../../redux/services/coreApi'
import { setFeeStructuresTableState } from '../../../../../redux/slices/tableFiltersSlice'
import type { RootState } from '../../../../../redux/store'
import CreateFeeStructureModal from './components/CreateFeeStructureModal'
import FeeStructureDetails from './components/FeeStructureDetails'

const FeeStructuresScreen: React.FC = () => {
  const { data: feeStructures, isLoading } = useGetFeeStructuresQuery()

  const [selectedFeeStructureId, setSelectedFeeStructureId] = useState<
    string | null
  >(null)

  const {
    data: selectedFeeStructureData,
    isLoading: isFetchingFeeStructureDetails,
  } = useGetFeeStructureByIdQuery(selectedFeeStructureId ?? '', {
    skip: !selectedFeeStructureId,
  })

  const selectedFeeStructure = selectedFeeStructureData ?? null

  const feeStructuresTableState = useSelector(
    (state: RootState) => state.feeStructuresTable
  )

  const createModal = useDisclosure()

  useEffect(() => {
    if (!feeStructures || feeStructures.length === 0) {
      setSelectedFeeStructureId(null)
      return
    }

    if (
      !selectedFeeStructureId ||
      !feeStructures.some(item => item.id === selectedFeeStructureId)
    ) {
      setSelectedFeeStructureId(feeStructures[0].id)
    }
  }, [feeStructures, selectedFeeStructureId])

  const tableData = useMemo(() => {
    return (
      feeStructures?.map(structure => ({
        ...structure,
        rollingWindow: structure.thirtyDayRolling ? '30-day' : 'Standard',
        baseFeeCollectionsCount: structure.baseFeeCollections.length,
        bankFeesCount: structure.bankFees.length,
      })) ?? []
    )
  }, [feeStructures])

  const schema = useMemo(
    () => [
      {
        cardTitle: true,
        header: 'Description',
        accessorKey: 'description',
        type: 'string' as const,
        priority: 1,
      },
      {
        header: 'Window',
        accessorKey: 'rollingWindow',
        type: 'string' as const,
        priority: 2,
      },
      {
        header: '# Base collections',
        accessorKey: 'baseFeeCollectionsCount',
        type: 'number' as const,
        priority: 3,
      },
      {
        header: '# Bank fees',
        accessorKey: 'bankFeesCount',
        type: 'number' as const,
        priority: 4,
      },
    ],
    []
  )

  return (
    <PageWrapper title="Fee Structures">
      <div className="flex flex-col lg:flex-row gap-6">
        <div className="lg:w-1/2 space-y-4">
          <Table
            tableState={feeStructuresTableState}
            setTableState={setFeeStructuresTableState}
            navigate={() => null}
            isLoading={isLoading}
            onClickRow={value => {
              if (!value?.id) return
              setSelectedFeeStructureId(value.id)
            }}
            defaultData={tableData}
            schema={schema}
            emptyDataRender={
              <div className="text-description text-center py-5">
                No fee structures found.
              </div>
            }
          />

          <div className="flex justify-end">
            <button
              className="button flex items-center gap-2"
              onClick={createModal.onOpen}
            >
              <AddCircleIcon width={18} height={18} color="currentColor" />
              Create fee structure
            </button>
          </div>
        </div>
        <div className="lg:flex-1">
          <FeeStructureDetails
            feeStructure={selectedFeeStructure}
            isLoading={isFetchingFeeStructureDetails}
            onUpdated={id => {
              setSelectedFeeStructureId(id)
            }}
            onDeleted={deletedId => {
              // Clear selection immediately if it matches the deleted ID
              if (selectedFeeStructureId === deletedId) {
                setSelectedFeeStructureId(null)
                // Select first available fee structure if any remain
                const remainingStructures = feeStructures?.filter(
                  fs => fs.id !== deletedId
                )
                if (remainingStructures && remainingStructures.length > 0) {
                  setSelectedFeeStructureId(remainingStructures[0].id)
                }
              }
            }}
          />
        </div>
      </div>

      <CreateFeeStructureModal
        isOpen={createModal.isOpen}
        onClose={createModal.onClose}
        onCreated={id => {
          createModal.onClose()
          setSelectedFeeStructureId(id)
        }}
      />
    </PageWrapper>
  )
}

export default FeeStructuresScreen
